import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Map as MapIcon } from 'lucide-react';
import { Button } from './ui/button';
import { ApiEndPoint } from '@/services/apiUttiLities';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// "The matched scenario includes a map — load it?" offer. Loading is an
// explicit user confirmation and is fully independent of the response-plan
// (timeline) activation: it can happen before or after Activate Response Plan.
// variant="button" renders a compact action for the map widget header;
// the default renders the full banner.
const ScenarioMapOfferPrompt = ({ incidentId, onApplied, variant = 'banner' }) => {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const skipKey = `scenario-map-skip-${incidentId}`;
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(skipKey) === '1');

  useEffect(() => {
    if (!incidentId) return;
    axios
      .get(`${ApiEndPoint}/api/incidents/${incidentId}/scenario-map-offer`, { headers: authHeaders() })
      .then((r) => setOffer(r.data?.data || null))
      .catch(() => setOffer(null));
  }, [incidentId]);

  const load = async () => {
    setBusy(true);
    try {
      await axios.post(`${ApiEndPoint}/api/incidents/${incidentId}/apply-scenario-map`, {}, { headers: authHeaders() });
      toast.success('Scenario map loaded into this incident');
      setOffer((o) => ({ ...o, applied: true }));
      // Tell the map widget to reload its zones/points/tasks immediately.
      window.dispatchEvent(new CustomEvent('gisMapUpdated', { detail: { incidentId } }));
      onApplied && onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load the scenario map');
    } finally {
      setBusy(false);
    }
  };

  if (!offer?.available) return null;

  const counts = [offer.tasks ? `${offer.tasks} task(s)` : null, offer.zones ? `${offer.zones} zone(s)` : null]
    .filter(Boolean).join(', ');

  if (variant === 'button') {
    if (offer.applied) return null;
    return (
      <Button
        size="sm"
        disabled={busy}
        className="h-7 text-xs px-2.5 bg-sky-600 hover:bg-sky-700 text-white"
        data-testid="activate-scenario-map-btn"
        title={`Load the map prepared in scenario${offer.scenario_name ? ` “${offer.scenario_name}”` : ''}${counts ? ` (${counts})` : ''} — independent of activating the response plan`}
        onClick={load}
      >
        <MapIcon className="h-3 w-3 mr-1" />
        {busy ? 'Loading…' : 'Activate scenario map'}
      </Button>
    );
  }

  if (dismissed || offer.applied) return null;

  return (
    <div className="mx-4 mt-3 p-4 rounded-xl border-2 border-sky-300 bg-gradient-to-r from-sky-50 to-blue-50 flex items-center justify-between gap-3" data-testid="scenario-map-offer">
      <div>
        <p className="text-sm font-bold text-sky-900 flex items-center gap-1.5">
          <MapIcon className="h-4 w-4" /> The matched scenario{offer.scenario_name ? ` “${offer.scenario_name}”` : ''} includes a map{counts ? ` (${counts})` : ''}.
        </p>
        <p className="text-xs text-sky-700 mt-0.5">
          Load it into this incident's map? This is separate from activating the response plan.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button size="sm" disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="load-scenario-map-btn" onClick={load}>
          Load map
        </Button>
        <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => { sessionStorage.setItem(skipKey, '1'); setDismissed(true); }}>
          Skip
        </Button>
      </div>
    </div>
  );
};

export default ScenarioMapOfferPrompt;
